import { forwardRef } from "react";

import { Box, StackProps, Stack, useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";

export type StorybookDialogPreviewProps = StackProps & {
  dialogWidth?: number;
  variant?: "default" | "popup" | "drawer";
};

export const StorybookDialogPreview = forwardRef<HTMLDivElement, StorybookDialogPreviewProps>(
  ({ sx, children, dialogWidth, variant = "default", ...rest }, ref) => {
    const theme = useTheme();
    const borderColor = theme.palette.formsComponents?.dialog?.borderColor;
    const isDrawer = variant === "drawer";

    return (
      <Box
        sx={{
          padding: { xs: 2, md: 4 },
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          ...(isDrawer && { display: "flex", alignItems: "flex-end", minHeight: 400 }),
        }}
      >
        <Stack
          ref={ref}
          {...rest}
          sx={mergeSx(
            {
              position: "relative",
              maxWidth: dialogWidth ?? 600,
              maxHeight: isDrawer ? undefined : 600,
              overflow: "hidden",
              marginInline: "auto",
              backgroundColor: theme.palette.formsComponents.dialog.backgroundColor,
              borderRadius: isDrawer ? "20px 20px 0 0" : theme.radius(1),
              boxShadow: theme.shadows[8],
              ...(isDrawer && { width: "100%", maxWidth: 375 }),
              ...(borderColor && {
                border: `1px solid ${borderColor}`,
                ...(isDrawer && { borderBottom: "none" }),
              }),
            },
            sx
          )}
        >
          {children}
        </Stack>
      </Box>
    );
  }
);

StorybookDialogPreview.displayName = "StorybookDialogPreview";
